import React, { forwardRef, useEffect, useState } from 'react';
import { Box, TextField, InputAdornment, IconButton, useTheme, alpha } from '@mui/material';
import { Search, Calendar } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';

interface Props {
  onSearch: (term: string, from: string, to: string) => void;
  resetSignal?: number;
}

const DateRangeInput = forwardRef<
  HTMLDivElement,
  { value?: string; onClick?: () => void; theme?: any }
>(({ value, onClick, theme }, ref) => (
  <TextField
    ref={ref}
    size="small"
    variant="outlined"
    placeholder="Select dates"
    value={value}
    onClick={onClick}
    sx={{
      minWidth: 240,
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: theme?.palette.background.paper,
        '&:hover': {
          backgroundColor: alpha(theme?.palette.primary.main || '#1a73e8', 0.04),
        },
      },
    }}
    InputProps={{
      readOnly: true,
      startAdornment: (
        <InputAdornment position="start">
          <Calendar size={18} color={theme?.palette.text.secondary} />
        </InputAdornment>
      ),
    }}
  />
));

export const BookingSearchBar: React.FC<Props> = ({ onSearch, resetSignal }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [readyToSearch, setReadyToSearch] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && readyToSearch) handleSearch();
  };
  const updateRange = (range: [Date | null, Date | null]) => {
    setDateRange(range);
    const [s, e] = range;
    // only enable when BOTH dates are picked
    setReadyToSearch(Boolean(s && e));
  };

  const handleSearch = () => {
    if (!readyToSearch || !startDate || !endDate) return;
    const start = format(startDate, 'yyyy-MM-dd');
    const end = format(endDate, 'yyyy-MM-dd');
    onSearch(searchTerm, start, end);
  };

  useEffect(() => {
    setSearchTerm('');
    setDateRange([null, null]);
    setReadyToSearch(false);
  }, [resetSignal]);

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      <DatePicker
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={updateRange}
        isClearable
        customInput={<DateRangeInput theme={theme} />}
        popperPlacement="bottom-start"
      />

      <TextField
        placeholder={readyToSearch ? 'Select dates to search' : 'Select dates to search'}
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={!readyToSearch}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={handleSearch}
                edge="end"
                disabled={!readyToSearch}
                sx={{
                  backgroundColor: readyToSearch
                    ? theme.palette.primary.main
                    : alpha(theme.palette.text.disabled, 0.1),
                  color: readyToSearch ? '#fff' : theme.palette.text.disabled,
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  mr: -0.5,
                  transition: 'all 200ms ease',
                  '&:hover': {
                    backgroundColor: readyToSearch
                      ? theme.palette.primary.dark
                      : alpha(theme.palette.text.disabled, 0.1),
                  },
                  '&.Mui-disabled': {
                    backgroundColor: alpha(theme.palette.text.disabled, 0.08),
                    color: theme.palette.text.disabled,
                  },
                }}
              >
                <Search size={16} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 220,
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            backgroundColor: theme.palette.background.paper,
            paddingRight: '8px',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
          },
        }}
      />
    </Box>
  );
};
